import * as React from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import {
  Bold as BoldIcon,
  Heading1,
  Heading2,
  Heading3,
  List as ListIcon,
  Quote as QuoteIcon,
  Pilcrow,
} from 'lucide-react';

export interface MarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  minHeight?: string;
  className?: string;
}

/**
 * DEV-95 — Keep external `value` in sync with the Tiptap editor when the
 * parent updates it (e.g. after AI generation). Only write when the editor
 * is NOT focused, so we don't clobber the user's in-progress edits.
 * Effects are allowed inside custom hooks per CLAUDE.md conventions.
 */
function useMarkdownSync(editor: Editor | null, value: string) {
  React.useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;
    const current = editor.storage.markdown?.getMarkdown?.() ?? '';
    if (current === value) return;
    editor.commands.setContent(value || '', false);
  }, [editor, value]);
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  active = false,
  disabled = false,
  label,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={active}
    aria-label={label}
    title={label}
    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      active
        ? 'bg-purple-600 text-white border-purple-600'
        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
    }`}
  >
    {children}
  </button>
);

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = '320px',
  className = '',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Markdown.configure({
        html: false,
        tightLists: true,
        bulletListMarker: '-',
        linkify: true,
        breaks: false,
        transformPastedText: true,
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'ProseMirror prose prose-sm max-w-none focus:outline-none px-4 py-3',
        'data-placeholder': placeholder ?? '',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const md = ed.storage.markdown?.getMarkdown?.() ?? '';
      onChange(md);
    },
  });

  useMarkdownSync(editor, value);

  if (!editor) {
    return (
      <div
        className={`border border-gray-300 rounded-lg bg-white ${className}`}
        style={{ minHeight }}
      />
    );
  }

  const isHeading = (level: 1 | 2 | 3) =>
    editor.isActive('heading', { level });

  return (
    <div
      className={`border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent ${className}`}
    >
      <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <ToolbarButton
          label="Heading 1"
          active={isHeading(1)}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <Heading1 className="w-3.5 h-3.5" />
          <span>H1</span>
        </ToolbarButton>
        <ToolbarButton
          label="Heading 2"
          active={isHeading(2)}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <Heading2 className="w-3.5 h-3.5" />
          <span>H2</span>
        </ToolbarButton>
        <ToolbarButton
          label="Heading 3"
          active={isHeading(3)}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          <Heading3 className="w-3.5 h-3.5" />
          <span>H3</span>
        </ToolbarButton>
        <ToolbarButton
          label="Body paragraph"
          active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          <Pilcrow className="w-3.5 h-3.5" />
          <span>Body</span>
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-gray-300" aria-hidden />
        <ToolbarButton
          label="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon className="w-3.5 h-3.5" />
          <span>Bold</span>
        </ToolbarButton>
        <ToolbarButton
          label="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListIcon className="w-3.5 h-3.5" />
          <span>List</span>
        </ToolbarButton>
        <ToolbarButton
          label="Quote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <QuoteIcon className="w-3.5 h-3.5" />
          <span>Quote</span>
        </ToolbarButton>
      </div>

      <div style={{ minHeight }} className="markdown-editor-surface">
        <EditorContent editor={editor} />
      </div>

      <style>{`
        .markdown-editor-surface .ProseMirror {
          min-height: ${minHeight};
          outline: none;
        }
        .markdown-editor-surface .ProseMirror p.is-editor-empty:first-child::before {
          color: #9ca3af;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .markdown-editor-surface .ProseMirror h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0.75rem 0 0.5rem;
          line-height: 1.25;
        }
        .markdown-editor-surface .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 0.75rem 0 0.5rem;
          line-height: 1.3;
        }
        .markdown-editor-surface .ProseMirror h3 {
          font-size: 1.075rem;
          font-weight: 600;
          color: #111827;
          margin: 0.6rem 0 0.4rem;
        }
        .markdown-editor-surface .ProseMirror p {
          color: #374151;
          line-height: 1.65;
          margin: 0.4rem 0;
        }
        .markdown-editor-surface .ProseMirror strong { font-weight: 700; color: #111827; }
        .markdown-editor-surface .ProseMirror em { font-style: italic; }
        .markdown-editor-surface .ProseMirror ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin: 0.4rem 0;
        }
        .markdown-editor-surface .ProseMirror ol {
          list-style: decimal;
          padding-left: 1.5rem;
          margin: 0.4rem 0;
        }
        .markdown-editor-surface .ProseMirror li { margin: 0.15rem 0; }
        .markdown-editor-surface .ProseMirror blockquote {
          border-left: 3px solid #c4b5fd;
          padding-left: 0.875rem;
          color: #4b5563;
          font-style: italic;
          margin: 0.5rem 0;
        }
        .markdown-editor-surface .ProseMirror code {
          background: #f3f4f6;
          padding: 0.1rem 0.3rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
        }
        .markdown-editor-surface .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default MarkdownEditor;
